/* eslint-disable @typescript-eslint/no-non-null-assertion */

import cn from 'classnames';
import * as React from 'react';
import Select from 'react-select';

import { Checkbox } from '../../checkbox';
import {
  customRepeatPeriodOptions,
  CustomRepeatPeriodSelectOption,
  CustomRepeatPeriodSelectSingleValue,
  customRepeatPeriodSelectStyles,
  getCustomRepeatPeriodLabel,
  getRepeatIntervalLabel,
  getRepeatPeriodTypeLabel,
  repeatIntervalOptions,
  RepeatIntervalSelectOption,
  RepeatIntervalSelectSingleValue,
  repeatIntervalSelectStyles,
  repeatPeriodTypeOptions,
  RepeatPeriodTypeSelectOption,
  RepeatPeriodTypeSelectSingleValue,
  repeatPeriodTypeSelectStyles,
  UserSelectDropDownIndicator,
} from '../../select-styles';
import { CalendarRepeat, OnChangeRepeatCallback } from '../types/calendar';
import { SelectDayOfTheMonth } from './SelectDayOfTheMonth';

const weekdayList = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const { useState } = React;
export const EditRepeatProps = {};

interface EditRepeatProps {
  customElements?: any;
  onChangeRepeat: OnChangeRepeatCallback;
  recurringData?: CalendarRepeat;
}

export const EditRepeat = ({ onChangeRepeat, recurringData }: EditRepeatProps) => {
  const [calendarRepeat, setCalendarRepeat] = useState<CalendarRepeat>(recurringData || {});

  const handleWeekdayChanged = (checked: boolean, id: string, value: string) => {
    const tempWeekdays = calendarRepeat.repeats || [];

    //TODO: make sure value is string
    const valueH = parseInt(value);
    if (tempWeekdays?.includes(valueH)) {
      // remove
      const index = tempWeekdays.indexOf(valueH);

      if (index !== -1) {
        tempWeekdays.splice(index, 1);
      }
    } else {
      //  add
      tempWeekdays?.push(valueH);
    }

    onChangeRepeat({ ...calendarRepeat, repeats: tempWeekdays });
    setCalendarRepeat({ ...calendarRepeat, repeats: tempWeekdays });
  };

  const selectWeekdays = React.useCallback(() => {
    return weekdayList.map((weekday, index) => (
      <div key={weekday} className="gap-6">
        <Checkbox
          wrapperClass="w-full flex flex-col-reverse mx-4"
          id={weekday}
          value={index}
          isRecurring
          onValueChanged={handleWeekdayChanged}
          checked={calendarRepeat?.repeats?.includes(weekdayList.indexOf(weekday))}
        >
          <div className="capitalize text-gray-500 font-medium">{weekday.substring(0, 2)}</div>
        </Checkbox>
      </div>
    ));
  }, [handleWeekdayChanged, calendarRepeat.repeats]);

  const handleRepeatTypeChange = (selected) => {
    if (selected.value === 'custom') {
      setCalendarRepeat({
        type: 'custom',
        custom: 'week',
        interval: 1,
        repeats: [0],
      });
      onChangeRepeat({
        type: 'custom',
        custom: 'week',
        interval: 1,
        repeats: [0],
      });
    } else if (selected.value === 'weekly') {
      setCalendarRepeat({ type: 'weekly', repeats: [0] });
      onChangeRepeat({ type: 'weekly', repeats: [0] });
    } else if (selected.value === 'monthly') {
      setCalendarRepeat({ type: 'monthly', repeats: [1] });
      onChangeRepeat({ type: 'monthly', repeats: [1] });
    } else {
      setCalendarRepeat({ type: selected.value });
      onChangeRepeat({ type: selected.value });
    }
  };

  const handleCustomChange = (selected) => {
    if (selected.value === 'week') {
      setCalendarRepeat({ ...calendarRepeat, custom: selected.value, repeats: [1] });
      onChangeRepeat({ ...calendarRepeat, custom: selected.value });
    } else if (selected.value === 'month') {
      setCalendarRepeat({ ...calendarRepeat, custom: selected.value, repeats: [1] });
      onChangeRepeat({ ...calendarRepeat, custom: selected.value });
    } else {
      setCalendarRepeat({ ...calendarRepeat, custom: selected.value, repeats: [] });
      onChangeRepeat({ ...calendarRepeat, custom: selected.value, repeats: [] });
    }
  };

  const handleChangePeriodicInterval = (days) => {
    setCalendarRepeat({ ...calendarRepeat, interval: days.value });
    onChangeRepeat({ ...calendarRepeat, interval: days.value });
  };

  const handleChangeMonth = (monthDay) => {
    setCalendarRepeat({ ...calendarRepeat, repeats: [monthDay] });
    onChangeRepeat({ ...calendarRepeat, repeats: [monthDay] });
  };

  return (
    <div className="flex flex-col gap-10">
      <Select
        options={[
          {
            label: 'Select a repeat for the task',
            options: repeatPeriodTypeOptions,
          },
        ]}
        menuPortalTarget={document.body}
        onChange={handleRepeatTypeChange}
        value={{
          label: getRepeatPeriodTypeLabel[calendarRepeat.type || 'weekly'],
          value: calendarRepeat.type,
        }}
        styles={repeatPeriodTypeSelectStyles}
        menuPlacement="bottom"
        placeholder="Choose repeat type"
        components={{
          DropdownIndicator: UserSelectDropDownIndicator,
          IndicatorSeparator: () => null,
          SingleValue: RepeatPeriodTypeSelectSingleValue,
          Option: RepeatPeriodTypeSelectOption,
        }}
      />

      {calendarRepeat.type === 'weekly' ? (
        <div className=" flex flex-col gap-10">
          <label className="text-sm text-gray-500 font-medium">On these days</label>
          <div className="flex flex-row w-full justify-center px-4 gap-6">{selectWeekdays()}</div>
        </div>
      ) : calendarRepeat.type === 'monthly' ? (
        <SelectDayOfTheMonth
          interval={calendarRepeat?.repeats?.[0].toString() || '1'}
          handleChangeMonth={handleChangeMonth}
        />
      ) : calendarRepeat.type === 'custom' ? (
        <div className="w-full flex flex-col gap-10">
          <label htmlFor="assigner" className={cn('text-sm text-gray-500 font-medium', {})}>
            Every
          </label>
          <div className="flex flex-row gap-10 w-full grow">
            <div className=" w-96">
              <Select
                className="w-full"
                options={[
                  {
                    label: 'interval',
                    options: repeatIntervalOptions(),
                  },
                ]}
                menuPortalTarget={document.body}
                // isDisabled={item?.isReadOnly || item?.access === 'view' || boardsStore.isPublicView}
                onChange={handleChangePeriodicInterval}
                value={
                  calendarRepeat.interval
                    ? {
                        label: getRepeatIntervalLabel(calendarRepeat.interval),
                        value: calendarRepeat.interval,
                      }
                    : { label: '01', value: 1 }
                }
                styles={repeatIntervalSelectStyles}
                menuPlacement="bottom"
                placeholder="select"
                components={{
                  DropdownIndicator: UserSelectDropDownIndicator,
                  IndicatorSeparator: () => null,
                  SingleValue: RepeatIntervalSelectSingleValue,
                  Option: RepeatIntervalSelectOption,
                }}
              />
            </div>
            <div className="w-120">
              <Select
                className="grow"
                options={[
                  {
                    label: 'select period',
                    options: customRepeatPeriodOptions,
                  },
                ]}
                menuPortalTarget={document.body}
                onChange={handleCustomChange}
                value={
                  calendarRepeat.custom
                    ? {
                        label: getCustomRepeatPeriodLabel[calendarRepeat.custom],
                        value: calendarRepeat.custom,
                      }
                    : {
                        label: 'Week',
                        value: 'week',
                      }
                }
                styles={customRepeatPeriodSelectStyles}
                menuPlacement="bottom"
                placeholder="select"
                components={{
                  DropdownIndicator: UserSelectDropDownIndicator,
                  IndicatorSeparator: () => null,
                  SingleValue: CustomRepeatPeriodSelectSingleValue,
                  Option: CustomRepeatPeriodSelectOption,
                }}
              />
            </div>
          </div>
        </div>
      ) : null}
      {calendarRepeat.custom === 'week' ? (
        <div className=" flex flex-col gap-10">
          <label htmlFor="assigner" className={cn('text-sm text-gray-500 font-medium', {})}>
            On these days
          </label>
          <div className="flex flex-row w-full justify-center gap-6 px-12">{selectWeekdays()}</div>
        </div>
      ) : calendarRepeat.custom === 'month' ? (
        <SelectDayOfTheMonth
          interval={calendarRepeat?.repeats?.[0].toString() || '1'}
          handleChangeMonth={handleChangeMonth}
        />
      ) : null}
    </div>
  );
};
